import { CallHistoryMethodAction, replace } from 'connected-react-router';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import {
	impersonateLogin as apiImpersonateLogin,
	ImpersonateLoginErrorResponse,
	switchBack as apiSwitchBack,
} from '~/Api/Authentication/Impersonate';
import { invokeApi } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { updateUserContext } from '~/Logger/actions';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';

export function impersonateLogin(
	targetUserId: string | null
): ThunkAction<Promise<void>, Action | CallHistoryMethodAction> {
	return async (dispatch, _, { persistentStateManager }) => {
		const redirectUrl = '/product-groups/';

		if (targetUserId === null) {
			dispatch(replace(redirectUrl));
			dispatch(flashErrorMessage('Something went wrong, please make sure you are using correct link'));
			return;
		}

		try {
			const response = await dispatch(fetchAccessTokenIfNeeded());
			if (response.accessToken.impersonatedBy !== null) {
				await dispatch(switchBack());
			}
		} catch (_) {
			// ignore invalid/expired access token
		}

		const request = dispatch(invokeApi(apiImpersonateLogin, targetUserId));
		dispatch({ type: ActionType.request, request });

		try {
			const response = await request;
			updateUserContext(response.accessToken);
			dispatch(invokeApi((apiClient) => apiClient.saveAccessToken(response.accessToken.token)));
			dispatch({
				type: ActionType.receive,
				request,
				accessToken: response.accessToken,
			});

			persistentStateManager.loadStateSafe(response.accessToken.user.id);

			dispatch(replace(redirectUrl));
		} catch (errorResponse) {
			dispatch(replace(redirectUrl));

			if (errorResponse instanceof ImpersonateLoginErrorResponse) {
				if (errorResponse.handleUserNotFound()) {
					dispatch(flashErrorMessage('Impersonated user not foud'));
				}
				if (errorResponse.handleUserNotRegistered()) {
					dispatch(flashErrorMessage('Impersonated user is not registered'));
				}
				if (errorResponse.handleNotAllowed()) {
					dispatch(flashErrorMessage('User is not allowed to be impersonated'));
				}
				if (errorResponse.handleAccessDenied()) {
					dispatch(
						flashErrorMessage(
							"Access denied. You're not allowed to impersonate other users. Make sure you're logged in as admin user with required permissions."
						)
					);
				}
				if (errorResponse.handleExternalServiceNotAllowed()) {
					dispatch(flashErrorMessage('Cannot impersonate service account'));
				}
			}
			dispatch({ type: ActionType.receive, request, accessToken: null });
			dispatch(handleError(errorResponse));
		}
	};
}

export function switchBack(): ThunkAction<Promise<void>, Action> {
	return async (dispatch, _, { persistentStateManager }) => {
		const request = dispatch(invokeApi(apiSwitchBack));
		dispatch({ type: ActionType.requestSwitchBack, request });

		try {
			const response = await request;
			updateUserContext(response.accessToken);
			dispatch(invokeApi((apiClient) => apiClient.saveAccessToken(response.accessToken.token)));
			dispatch({
				type: ActionType.receiveSwitchBack,
				request,
				accessToken: response.accessToken,
			});

			persistentStateManager.loadStateSafe(response.accessToken.user.id);
		} catch (errorResponse) {
			dispatch({
				type: ActionType.receiveSwitchBack,
				request,
				accessToken: null,
			});
		}
	};
}
